import type { BrandingConfig } from "../types";

/**
 * Convert hex color to HSL values (for Tailwind CSS variables).
 * Returns format "H S% L%".
 */
export function hexToHSL(hex: string): string {
  hex = hex.replace(/^#/, "");

  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Determine if a color is light or dark (for foreground contrast).
 */
export function isLightColor(hex: string): boolean {
  hex = hex.replace(/^#/, "");
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5;
}

/**
 * Apply branding colors to CSS custom properties on document root.
 * Pass null to reset to defaults.
 */
export function applyBrandingColors(branding: BrandingConfig | null): void {
  const root = document.documentElement;

  if (!branding) {
    root.style.removeProperty("--primary");
    root.style.removeProperty("--primary-foreground");
    root.style.removeProperty("--accent");
    root.style.removeProperty("--accent-foreground");
    return;
  }

  if (branding.primary_color) {
    const hsl = hexToHSL(branding.primary_color);
    root.style.setProperty("--primary", hsl);
    const foreground = isLightColor(branding.primary_color)
      ? "0 0% 0%"
      : "0 0% 100%";
    root.style.setProperty("--primary-foreground", foreground);
  }

  if (branding.accent_color) {
    const hsl = hexToHSL(branding.accent_color);
    root.style.setProperty("--accent", hsl);
    const foreground = isLightColor(branding.accent_color)
      ? "0 0% 0%"
      : "0 0% 100%";
    root.style.setProperty("--accent-foreground", foreground);
  }
}
